// src/components/Navbar.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Settings, Shield, User, Menu, Bell, Accessibility, LogOut, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { AuthButton } from './auth/auth-button';
import { useResponsive } from '@/hooks/useResponsive';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/providers/AuthProvider';
import { auth } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { SkipLinks } from '@/components/ui/accessibility/skip-link';
import { useAnnouncement } from '@/components/ui/accessibility/live-region';
import { KeyboardNavigation, useKeyboardNavigation } from '@/components/ui/accessibility/keyboard-navigation';
import { useTheme } from '@/providers/theme-provider';
import { NotificationDropdown } from './notifications/notification-dropdown';
import { MessagesDropdown } from './messages/messages-dropdown';
import { CurrencySelector } from './currency/currency-selector';
import { LanguageSelector } from './language/LanguageSelector';
import { UserSearch } from './search/user-search';
import { AccessibilityDropdown } from './accessibility/accessibility-dropdown';

interface NavbarProps {
  onMenuClick?: () => void;
  onNavigate?: (path: string) => void;
  showDesktopMenu?: boolean;
  menuExpanded?: boolean;
}

export function Navbar({ onMenuClick, onNavigate, showDesktopMenu = false, menuExpanded = false }: NavbarProps) {
  const { t } = useTranslation();
  const { announce } = useAnnouncement();
  const { user, userData } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);
  
  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    }
    announce(t('navbar.navigationMenu'));
  };

  const handleNavigation = (path: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      announce(t('common.logout'));
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      announce(t('common.error'));
    }
  };

  const { isTablet, isMobile } = useResponsive();
  const { isRTL } = useLanguage();
  const resolvedAvatar = userData?.photoURL || user?.photoURL || '';
  const resolvedName = userData?.name || user?.displayName || 'User';

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
    announce(t('navbar.theme'));
  };

  return (
    <>
      <SkipLinks />
      <header 
        className="fixed top-0 left-0 right-0 z-50 border-b border-blue-100/70 bg-white/82 shadow-[0_10px_30px_-22px_rgba(15,23,42,0.35)] backdrop-blur-xl dark:border-slate-700/70 dark:bg-slate-900/82"
        role="banner"
      >
        <nav 
          className="flex w-full items-center justify-between px-2 py-3 sm:px-3 lg:px-4"
          aria-label="Main navigation"
        >
          {/* Fullscreen Exchange also needs desktop access to navigation. */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMenuClick}
              className={`${showDesktopMenu ? '' : 'lg:hidden '}h-10 w-10 rounded-xl bg-gradient-to-b from-blue-500 to-blue-600 p-2.5 text-white shadow-[0_12px_26px_-14px_rgba(37,99,235,0.85)] hover:from-blue-500 hover:to-blue-700`}
              aria-label={t('navbar.toggleMenu')}
              aria-expanded={menuExpanded}
              aria-controls="sidebar-nav"
            >
              <Menu className="w-6 h-6" />
            </Button>

            <button
              type="button"
              onClick={() => handleNavigation('/')}
              className="text-2xl font-bold text-blue-700 dark:text-blue-200 whitespace-nowrap hover:opacity-80 transition-opacity"
              aria-label="InTouch - Go to home page"
            >
              InTouch
            </button>

            {/* Logo - Updated design to match screenshot */}
            <button
              onClick={() => handleNavigation('/')}
              className="flex items-center hover:opacity-80 transition-opacity"
              aria-label="InTouch logo - Go to home page"
            >
              <div className="w-10 h-10 overflow-hidden shadow-sm bg-transparent rounded-none">
                <img
                  src="/Logo.png"
                  alt="InTouch logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </button>
          </div>

          {/* Search - Hidden on mobile */}
          {!isMobile && (
            <div className="flex-1 max-w-2xl mx-4 flex items-center gap-2">
              <div className="relative flex-1">
                <UserSearch />
              </div>
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
                }}
                className="hidden lg:flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium text-slate-500 hover:text-blue-600 bg-slate-100/90 hover:bg-blue-50/80 border border-slate-200/80 rounded-xl transition-all dark:bg-slate-800/80 dark:border-slate-700/80 dark:text-slate-400 dark:hover:text-blue-300 shadow-sm flex-shrink-0"
                title="Open Spotlight Search (⌘K)"
              >
                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500">⌘</span>
                <span>K</span>
              </button>
            </div>
          )}

          {/* Mobile Search & Spotlight Toggle */}
          {isMobile && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
                }}
                className="h-10 w-10 rounded-xl border border-blue-200/80 bg-blue-50/85 p-2.5 text-blue-700 hover:bg-blue-100 dark:border-blue-400/25 dark:bg-blue-500/10 dark:text-blue-200"
                aria-label="Open Command Search"
              >
                <Search className="w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Right section */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Desktop utilities */}
            {!isTablet && (
              <div className="flex items-center gap-1 mr-2">
                <CurrencySelector />
                <LanguageSelector />
              </div>
            )}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="h-10 w-10 rounded-xl border border-blue-200/80 bg-blue-50/85 p-2 text-blue-700 hover:bg-blue-100 dark:border-blue-400/25 dark:bg-blue-500/10 dark:text-blue-200 dark:hover:bg-blue-500/20"
              aria-label={t('navbar.theme')}
              title={t('navbar.theme')}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>

            {/* Accessibility Settings */}
            {!isMobile && (
              <AccessibilityDropdown />
            )}

            {/* Messages */}
            <div role="region" aria-label="Messages">
              <MessagesDropdown />
            </div>

            {/* Notifications Dropdown */}
            <div role="region" aria-label="Notifications">
              <NotificationDropdown />
            </div>

            {/* User Menu */}
            <div ref={userMenuRef} className="relative">
                <button
                  type="button"
                  className="relative h-10 w-10 rounded-full border border-blue-200/70 bg-white/85 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800/80 dark:hover:bg-slate-700"
                  aria-label={`User menu for ${resolvedName || 'user'}`}
                  aria-expanded={showUserMenu}
                  onClick={() => setShowUserMenu((prev) => !prev)}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={resolvedAvatar} alt={`${resolvedName} avatar`} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {resolvedName?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-60 rounded-2xl border border-blue-100/80 bg-white/95 p-1.5 text-popover-foreground shadow-[0_20px_48px_-24px_rgba(15,23,42,0.45)] backdrop-blur-xl dark:border-slate-700/80 dark:bg-slate-900/90 z-[120]" aria-label="User account menu">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleNavigation(userData?.username ? `/profile/${userData.username}` : '/profile');
                  }}
                  className="relative flex w-full items-center rounded-xl px-2.5 py-2 text-sm outline-none transition-colors hover:bg-blue-50 hover:text-blue-800 dark:hover:bg-blue-500/20 dark:hover:text-blue-100"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('navbar.profile')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleNavigation('/settings/privacy');
                  }}
                  className="relative flex w-full items-center rounded-xl px-2.5 py-2 text-sm outline-none transition-colors hover:bg-blue-50 hover:text-blue-800 dark:hover:bg-blue-500/20 dark:hover:text-blue-100"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {t('sidebar.privacySettings')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleNavigation('/settings');
                  }}
                  className="relative flex w-full items-center rounded-xl px-2.5 py-2 text-sm outline-none transition-colors hover:bg-blue-50 hover:text-blue-800 dark:hover:bg-blue-500/20 dark:hover:text-blue-100"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {t('navbar.settings')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleNavigation('/accessibility');
                  }}
                  className="relative flex w-full items-center rounded-xl px-2.5 py-2 text-sm outline-none transition-colors hover:bg-blue-50 hover:text-blue-800 dark:hover:bg-blue-500/20 dark:hover:text-blue-100"
                >
                  <Accessibility className="w-4 h-4 mr-2" />
                  {t('navbar.accessibility')}
                </button>
                <div className="-mx-1 my-1 h-px bg-muted" />
                <button
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className="relative flex w-full items-center rounded-xl px-2.5 py-2 text-sm text-destructive outline-none transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('navbar.logout')}
                </button>
              </div>
              )}
            </div>
          </div>
        </nav>

        {/* Mobile Search Bar */}
        {isMobile && showSearch && (
          <div className="border-t border-blue-100/70 px-4 pb-3 pt-3 dark:border-slate-700">
            <UserSearch />
          </div>
        )}
      </header>
    </>
  );
}
